/**
 * Recognise flatten-format screenplay text pasted into the Refine box.
 *
 * The Refine box feeds `in_feedback`, which the backend treats as a *targeted
 * revision* — `edit_screenplay_system_prompt.md` tells the model to keep the
 * existing scene count and preserve every scene the revision does not mention.
 * A user who pastes a whole rewritten screenplay means the opposite: replace
 * what is there, including dropping scenes. Detecting that shape lets the node
 * ask which one the user meant instead of silently picking the patch reading
 * (Workplace post 1597061732051124).
 *
 * The heading pattern mirrors the backend parser's `_FLAT_HEADER_RE`
 * (`script_writer/agent.py`) so the two agree on what "looks like a screenplay"
 * means — anything this predicate accepts, the script resync actions can parse.
 *
 * Not to be confused with `parseScreenplayText` in this folder: that reads the
 * *streaming* grammar (`INT. LOCATION - TIME` headings) for progressive scene
 * cards. `out_script` — what the resync action round-trips — uses the flatten
 * grammar emitted by `_flatten_screenplay`, whose heading anchor is `SCENE N`.
 */

const SCENE_HEADING_RE = /^[ \t]*SCENE[ \t]+\d+\b/

/** Number of `SCENE N` headings in `text`. */
export function countSceneHeadings(text: string): number {
  let count = 0
  for (const line of text.split('\n')) {
    if (SCENE_HEADING_RE.test(line)) count += 1
  }
  return count
}

/**
 * True when `text` reads as a complete screenplay rather than an instruction.
 *
 * Two headings is the threshold: one `SCENE 3` line is far more likely to be a
 * reference inside a sentence ("tighten SCENE 3") than a pasted script.
 */
export function looksLikeFullScreenplay(text: string): boolean {
  return countSceneHeadings(text) >= 2
}
